import { useCallback, useState } from 'react';

/**
 * localStorage keys for the Sample Comments column visibility (BM-9882). The
 * preference is global across every sample: one flag for Licensing, one for
 * Customer. Absent (first visit) reads as hidden, so only Internal is open by default.
 */
const LICENSING_KEY = 'sampleComments.showLicensing';
const CUSTOMER_KEY = 'sampleComments.showCustomer';

/**
 * Reads a boolean flag from localStorage, defaulting to hidden when unset or on
 * any access error (private mode, quota). Only an explicit 'true' shows.
 *
 * @param {string} key - localStorage key.
 * @returns {boolean} Stored visibility, false when unset or storage is unavailable.
 */
const readFlag = (key: string): boolean => {
  try {
    return window.localStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
};

/**
 * Persists a boolean flag to localStorage, swallowing access errors so a failed
 * write never breaks the toggle interaction.
 *
 * @param {string} key - localStorage key.
 * @param {boolean} value - Visibility to persist.
 * @returns {void}
 */
const writeFlag = (key: string, value: boolean): void => {
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    /* localStorage unavailable — keep the in-memory state only. */
  }
};

export interface CommentColumnToggle {
  showLicensing: boolean;
  showCustomer: boolean;
  toggleLicensing: () => void;
  toggleCustomer: () => void;
}

/**
 * Show/hide state for the Licensing and Customer comment columns, persisted
 * globally in localStorage so a user's choice is restored across samples and
 * sessions.
 *
 * @returns {CommentColumnToggle} Current visibility flags and their togglers.
 */
export const useCommentColumnToggle = (): CommentColumnToggle => {
  const [showLicensing, setShowLicensing] = useState<boolean>(() => readFlag(LICENSING_KEY));
  const [showCustomer, setShowCustomer] = useState<boolean>(() => readFlag(CUSTOMER_KEY));

  const toggleLicensing = useCallback(() => {
    setShowLicensing((prev) => {
      const next = !prev;
      writeFlag(LICENSING_KEY, next);
      return next;
    });
  }, []);

  const toggleCustomer = useCallback(() => {
    setShowCustomer((prev) => {
      const next = !prev;
      writeFlag(CUSTOMER_KEY, next);
      return next;
    });
  }, []);

  return { showLicensing, showCustomer, toggleLicensing, toggleCustomer };
};
